import axios from "axios";
import { useEffect, useMemo } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../Navbar";
import InventoryCalculationTable from "./InventoryCalculationTable";
import Footer from "../../Footer";
import { useFilter } from "../../../context/FilterContext";
import OutletSearch from "../../SelectSearch/OutletSearch";
import { CgSpinner } from "react-icons/cg";

const InventoryCalculation = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem('token');
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const navigate = useNavigate();

    const { filter, setFilter } = useFilter();

    const [consumptionData, setConsumptionData] = useState(null);
    const [inventoryData, setInventoryData] = useState([]);
    const [hasFoodPhysical, setHasFoodPhysical] = useState(false);
    const [foodOpeningDate, setFoodOpeningDate] = useState();
    const [hasBeveragePhysical, setHasBeveragePhysical] = useState(false);
    const [beverageOpeningDate, setBeverageOpeningDate] = useState();
    const [itemDetails, setItemDetails] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [foodStartDate, setFoodStartDate] = useState();
    const [beverageStartDate, setBeverageStartDate] = useState();
    const [foodPeriod, setFoodPeriod] = useState({ start: null, end: null });
    const [beveragePeriod, setBeveragePeriod] = useState({ start: null, end: null });
    const [checkEndDate, setCheckEndDate] = useState();
    const [fetchOnLoad, setFetchOnLoad] = useState(false);
    const [itemSearchTerm, setItemSearchTerm] = useState("");
    const [groupSearchTerm, setGroupSearchTerm] = useState("");
    const [includeSubRecipe, setIncludeSubRecipe] = useState(true);

    const getEndpoint = (base) => {
        return `${base}-${filter.toLowerCase()}`;
    };

    const api = {
        inventory: getEndpoint('inventory-summary'),
        save: getEndpoint('save-inventory-snapshot'),
    };

    const fetchDates = async () => {
        if (!token || !selectedOutlet) return;
        setStartDate(null);
        setEndDate(null);

        try {
            setLoading(true);

            const foodResponse = await axios.post(`${baseUrl}/get-inventory-period-food`, {
                token: token,
                outlet: selectedOutlet
            });

            const beverageResponse = await axios.post(`${baseUrl}/get-inventory-period-beverage`, {
                token: token,
                outlet: selectedOutlet
            });

            const foodData = foodResponse.data;
            const foodIsoStart = formatToISODate(foodData?.opening_date);
            const foodIsoEnd = formatToISODate(foodData?.closing_date);
            setFoodStartDate(foodIsoStart);

            const beverageData = beverageResponse.data;
            const beverageIsoStart = formatToISODate(beverageData?.opening_date);
            const beverageIsoEnd = formatToISODate(beverageData?.closing_date);
            setBeverageStartDate(beverageIsoStart);

            if (filter.toLowerCase() === "food") {
                setStartDate(foodIsoStart);
                setEndDate(foodIsoEnd);
            } else if (filter.toLowerCase() === "beverage") {
                setStartDate(beverageIsoStart);
                setEndDate(beverageIsoEnd);
            }

            setError(null);
            setFetchOnLoad(true);

        } catch (err) {
            setError(err?.response?.data?.message || err?.response?.data?.error);
            toast.error("Failed to fetch dates");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDates();
    }, [token, selectedOutlet])

    useEffect(() => {
        if (fetchOnLoad) {
            handleSearch();
            fetchInventoryData();
            setFetchOnLoad(false);
        }
    }, [token, selectedOutlet, includeSubRecipe, fetchOnLoad])

    const handleSearch = async (start = startDate, end = endDate) => {
        if (!token || !selectedOutlet || !start || !end) return;

        try {
            setLoading(true);
            const response = await axios.post(`${baseUrl}/menu-consumption`, {
                startDate: start,
                endDate: end,
                token: token,
                includeSubRecipeItems: includeSubRecipe,
                outlet: selectedOutlet
            });
            const data = response.data;
            setConsumptionData(data);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Failed to fetch data");
            setConsumptionData(null);
            toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const fetchInventoryData = async () => {
        if (!token || !selectedOutlet) return;
        if (!endDate || !foodStartDate || !beverageStartDate) return;

        try {
            setLoading(true);
            setItemSearchTerm('');
            setGroupSearchTerm('');

            const [foodRes, beverageRes] = await Promise.all([
                axios.post(`${baseUrl}/inventory-summary-food`, {
                    token,
                    includeSubRecipeItems: includeSubRecipe,
                    outlet: selectedOutlet,
                    startDate: foodStartDate,
                    endDate: endDate
                }),
                axios.post(`${baseUrl}/inventory-summary-beverage`, {
                    token,
                    includeSubRecipeItems: includeSubRecipe,
                    outlet: selectedOutlet,
                    startDate: beverageStartDate,
                    endDate: endDate
                })
            ]);

            const mergeGroupsIntoCategory = (summary, targetCategory) => {
                const merged = {};

                Object.values(summary || {}).forEach((categoryGroups) => {
                    Object.entries(categoryGroups || {}).forEach(([groupName, items]) => {
                        if (!merged[groupName]) {
                            merged[groupName] = [];
                        }
                        merged[groupName] = merged[groupName].concat(items);
                    });
                });

                return { [targetCategory]: merged };
            };

            const foodData = foodRes.data;
            const beverageData = beverageRes.data;

            const normalizedFood = mergeGroupsIntoCategory(foodData.summary, "Food");
            const normalizedBeverage = mergeGroupsIntoCategory(beverageData.summary, "Beverage");

            const combinedData = {
                summary: {
                    ...normalizedFood,
                    ...normalizedBeverage
                }
            };

            setInventoryData(combinedData);

            setHasFoodPhysical(foodData.has_physical_before_start);
            setFoodOpeningDate(foodData.first_opening_dates?.end_date);

            setHasBeveragePhysical(beverageData.has_physical_before_start);
            setBeverageOpeningDate(beverageData.first_opening_dates?.end_date);

            setFoodPeriod({
                start: foodData.period?.start_date,
                end: foodData.period?.end_date
            });
            setBeveragePeriod({
                start: beverageData.period?.start_date,
                end: beverageData.period?.end_date
            });

            setCheckEndDate(endDate);
            setError(null);

        } catch (err) {
            const errorMsg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err.message ||
                "Failed to fetch data";

            setError(errorMsg);
            setInventoryData([]);
            toast.error(errorMsg);

        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (e, type) => {
        const value = e.target.value;
        if (type === 'start') {
            setStartDate(value);
        } else {
            setEndDate(value);
        }
    };

    const [savedPhysicalCounts, setSavedPhysicalCounts] = useState(() => {
        try {
            const raw = localStorage.getItem('savedPhysicalCount');
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    const physicalMap = useMemo(() => {
        return Object.fromEntries(
            savedPhysicalCounts.map(i => [i.item_name, Number(i.quantity)])
        );
    }, [savedPhysicalCounts]);

    const getValidPhysical = (itemName) => {
        const num = physicalMap[itemName];
        return Number.isFinite(num) ? num : null;
    };

    const calculateDiscrepancy = (physicalCount, closingCount) => {
        return physicalCount - Number(closingCount);
    };

    const areAllCountsEmpty = () => {
        return savedPhysicalCounts.every(entry => {
            const qty = Number(entry?.quantity);
            return qty === 0 || isNaN(qty);
        });
    };

    const fetchItemData = async (item) => {
        try {
            const response = await axios.post(`${baseUrl}/get-menu-item`, {
                token: token,
                restaurant: selectedOutlet,
                name: item.ItemName
            })

            const data = response.data || [];
            localStorage.setItem('item', data.Name);
            localStorage.setItem('group', data.Description);
            window.open("/itemdata", "_blank");
        } catch (error) {
            console.log(error);
        }
    }

    const handleFetchDetails = async (name, category) => {
        try {
            const response = await axios.post(`${baseUrl}/itemconsumefromsale`, {
                token,
                outlet: selectedOutlet,
                item_name: name,
                startDate,
                endDate,
                item_type: category
            });

            const fetchedData = response.data || {};

            setItemDetails(fetchedData);

            setShowModal(true);
        } catch (error) {
            console.log(error);
            toast.error(error?.response?.data?.error || error?.response?.data?.message || "Failed to fetch details");
        }
    };

    const handleSave = () => {
        if (filter === "food" && startDate !== foodStartDate) {
            toast.error(
                `Start date mismatch. Selected: ${startDate}, Expected (Food Opening): ${foodStartDate}`
            );
            return;
        }

        if (filter === "beverage" && startDate !== beverageStartDate) {
            toast.error(
                `Start date mismatch. Selected: ${startDate}, Expected (Beverage Opening): ${beverageStartDate}`
            );
            return;
        }

        if (endDate !== checkEndDate) {
            toast.error(
                `End date mismatch. Selected: ${endDate}, Expected: ${checkEndDate}. Please search before saving.`
            );
            return;
        }

        if (areAllCountsEmpty()) {
            toast.error("Cannot save. No closing count has been recorded.");
            return;
        }

        handleSubmit();
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save inventory data?</p>

                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();

                            if (!token || !selectedOutlet || !inventoryData?.summary) return;

                            try {
                                setLoading(true);

                                const to12Decimals = (value) => Number((Number(value) || 0).toFixed(12));

                                const items = Object.entries(inventoryData.summary)
                                    .filter(([category]) => category.toLowerCase() === filter.toLowerCase())
                                    .flatMap(([category, groups]) =>
                                        Object.entries(groups).flatMap(([groupName, items]) =>
                                            items.map(item => {
                                                const physicalCount = getValidPhysical(item.name, savedPhysicalCounts);

                                                return {
                                                    name: item.name,
                                                    type: category,
                                                    group_name: groupName,
                                                    uom: item.primary_unit,
                                                    new_uom: item.new_uom || item.uom,

                                                    total_consumed: to12Decimals(item.total_consumed),
                                                    total_received: to12Decimals(item.total_received),
                                                    closing_balance: to12Decimals(item.closing_balance),
                                                    physical_count: to12Decimals(physicalCount),
                                                    opening_count: to12Decimals(item.opening),
                                                    total_wastage: to12Decimals(item.total_wasted),
                                                    discrepancy_count: to12Decimals(calculateDiscrepancy(physicalCount, item.closing_balance)),
                                                };
                                            })
                                        )
                                    );

                                const payload = {
                                    token: token,
                                    outlet: selectedOutlet,
                                    start_date: startDate,
                                    end_date: endDate,
                                    items: items,
                                    physical_added: !areAllCountsEmpty(),

                                    ...(filter === "food"
                                        ? {
                                            food_accuracy: foodPercentage,
                                            food_total_items: totalFoodItems,
                                            food_missing_items: foodRecipeCount
                                        }
                                        : {
                                            beverage_accuracy: beveragePercentage,
                                            beverage_total_items: totalBeverageItems,
                                            beverage_missing_items: beverageRecipeCount
                                        })
                                };

                                await axios.post(`${baseUrl}/${api.save}`, payload);

                                localStorage.removeItem('savedPhysicalCount');
                                setSavedPhysicalCounts([]);
                                setInventoryData([]);

                                fetchDates();

                                toast.success("Inventory snapshot saved successfully");

                            } catch (err) {
                                console.error("Error saving inventory:", err);

                                const errorMessage =
                                    err?.response?.data?.error ||
                                    err?.response?.data?.message ||
                                    err?.error ||
                                    "Failed to save inventory snapshot";

                                toast.error(errorMessage);

                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-inventory-data'
            }
        );
    };

    const formatToISODate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const handleReceivedClick = () => {
        navigate('/inventoryreceived');
    };

    const handleOpeningClick = () => {
        navigate('/addopeningcount', {
            state: {
                inventoryData,
                selectedOutlet,
                accessToken: "a7fK2pX9Zd",
                food_start_date: foodOpeningDate,
                beverage_start_date: beverageOpeningDate,
            }
        });
    };

    const handleWastageClick = () => {
        navigate('/add-wastage', {
            state: {
                startDate
            }
        });
    };

    const handlePhysicalClick = () => {
        navigate('/addphysicalcount', {
            state: {
                inventoryData,
                selectedOutlet,
                accessToken: "a7fK2pX9Zd"
            }
        });
    }

    const handleFilterChange = (selectedFilter) => {
        setFilter(selectedFilter);

        if (selectedFilter === "food") {
            const sameDates =
                startDate === foodStartDate;

            if (!sameDates) {
                setFetchOnLoad(true);
                setStartDate(foodStartDate);
                handleSearch(foodStartDate, endDate);
            }
        } else if (selectedFilter === "beverage") {
            const sameDates =
                startDate === beverageStartDate;

            if (!sameDates) {
                setFetchOnLoad(true);
                setStartDate(beverageStartDate);
                handleSearch(beverageStartDate, endDate);
            }
        }
    };

    const areAllOpeningCountsZero = (summary, filter) => {
        if (!summary || Object.keys(summary).length === 0) return false;

        if (!Object.keys(summary).some(cat => cat.toLowerCase() === filter.toLowerCase())) return false;

        return Object.entries(summary)
            .filter(([category]) => category.toLowerCase() === filter.toLowerCase())
            .every(([category, groups]) =>
                Object.entries(groups).every(([groupName, items]) =>
                    items.every(item => (item.opening ?? 0) === 0)
                )
            );
    };
    const allZero = areAllOpeningCountsZero(inventoryData.summary, filter);

    const totalFoodItems = consumptionData?.grouped_by_item_type?.Food?.unique_items_sold || 0;
    const foodRecipeCount = consumptionData?.grouped_by_item_type?.Food?.itemwise_summary?.filter(item => !item?.has_recipe).length || 0;
    const foodPercentage = totalFoodItems > 0
        ? ((totalFoodItems - foodRecipeCount) * 100 / totalFoodItems).toFixed(2)
        : 0;

    const totalBeverageItems = consumptionData?.grouped_by_item_type?.Beverage?.unique_items_sold || 0;
    const beverageRecipeCount = consumptionData?.grouped_by_item_type?.Beverage?.itemwise_summary?.filter(item => !item?.has_recipe).length || 0;
    const beveragePercentage = totalBeverageItems > 0
        ? ((totalBeverageItems - beverageRecipeCount) * 100 / totalBeverageItems).toFixed(2)
        : 0;

    const physicalFlags = {
        food: hasFoodPhysical,
        beverage: hasBeveragePhysical
    };

    return (
        <div className='flex flex-col min-h-screen'>
            <Navbar />

            <div className='flex-grow px-4 py-3'>
                <div className='flex items-center gap-14 text-sm'>
                    <div className="flex items-center gap-2">
                        <div className="whitespace-nowrap">From :</div>
                        <input
                            type="date"
                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={startDate}
                            onChange={(e) => handleDateChange(e, 'start')}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="whitespace-nowrap">To :</div>
                        <input
                            type="date"
                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={endDate}
                            onChange={(e) => handleDateChange(e, 'end')}
                        />
                    </div>

                    <OutletSearch
                        selectedOutlet={selectedOutlet}
                        setSelectedOutlet={setSelectedOutlet}
                        token={token}
                        compact={true}
                    />

                    <button
                        onClick={() => {
                            handleSearch(startDate, endDate);
                            fetchInventoryData();
                        }}
                        className="px-[16px] py-[7px] rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
                    >
                        Search
                    </button>

                    <div className='flex items-center gap-2'>
                        <input
                            type="checkbox"
                            checked={includeSubRecipe}
                            onChange={() => {
                                setFetchOnLoad(true)
                                setIncludeSubRecipe(!includeSubRecipe)
                            }}
                            className="cursor-pointer"
                        />
                        <label>Include Sub-recipe?</label>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center gap-2 py-4 text-gray-600">
                        <CgSpinner className="h-6 w-6 animate-spin" />
                        <span>Loading data...</span>
                    </div>
                ) : error ? (
                    <div className="text-red-500 py-4">Error: {error}</div>
                ) : consumptionData && (
                    <InventoryCalculationTable
                        consumptionData={consumptionData}
                        fetchItemData={fetchItemData}
                        itemSearchTerm={itemSearchTerm}
                        setItemSearchTerm={setItemSearchTerm}
                        filter={filter}
                        groupSearchTerm={groupSearchTerm}
                        setGroupSearchTerm={setGroupSearchTerm}
                        handleReceivedClick={handleReceivedClick}
                        inventoryData={inventoryData}
                        handleSave={handleSave}
                        handleOpeningClick={handleOpeningClick}
                        allZero={allZero}
                        handleWastageClick={handleWastageClick}
                        handleFetchDetails={handleFetchDetails}
                        itemDetails={itemDetails}
                        showModal={showModal}
                        setShowModal={setShowModal}
                        foodPercentage={foodPercentage}
                        foodRecipeCount={foodRecipeCount}
                        totalFoodItems={totalFoodItems}
                        beveragePercentage={beveragePercentage}
                        beverageRecipeCount={beverageRecipeCount}
                        totalBeverageItems={totalBeverageItems}
                        handlePhysicalClick={handlePhysicalClick}
                        hasPhysical={physicalFlags[filter] || false}
                        foodPeriod={foodPeriod}
                        beveragePeriod={beveragePeriod}
                        handleFilterChange={handleFilterChange}
                        endDate={checkEndDate}
                        getValidPhysical={getValidPhysical}
                        savedPhysicalCounts={savedPhysicalCounts}
                        calculateDiscrepancy={calculateDiscrepancy}
                        selectedOutlet={selectedOutlet}
                        baseUrl={baseUrl}
                        token={token}
                        areAllCountsEmpty={areAllCountsEmpty}
                    />
                )}
            </div>

            <Footer />
        </div>
    );
};

export default InventoryCalculation;